import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Upload, Brain, Loader2, Sparkles, Eye, Download, Calendar, X, Camera, RotateCcw, Check } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const MedicalReports = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [formData, setFormData] = useState({
    report_type: 'blood_test',
    report_name: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [file, setFile] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  const handleUpload = async () => {
    if (!file || !formData.report_name) {
      toast.error('Please fill all fields and select a file');
      return;
    }

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      formDataObj.append('report_type', formData.report_type);
      formDataObj.append('report_name', formData.report_name);
      formDataObj.append('date', formData.date);

      await axios.post(`${API}/reports/upload`, formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Report uploaded successfully!');
      loadReports();
      setFile(null);
      setFormData({
        report_type: 'blood_test',
        report_name: '',
        date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      toast.error('Failed to upload report');
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async (report) => {
    setAnalyzing(report.id);
    try {
      const response = await axios.post(`${API}/reports/${report.id}/analyze`);
      toast.success('AI Analysis completed!');
      
      // Update the report with analysis
      const updatedReports = reports.map(r => 
        r.id === report.id ? { ...r, analysis: response.data.analysis } : r
      );
      setReports(updatedReports);
      
      // Show analysis dialog
      setSelectedReport({ ...report, analysis: response.data.analysis });
      setShowAnalysis(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to analyze report');
    } finally {
      setAnalyzing(null);
    }
  };

  const canAnalyze = (reportType) => {
    // All report types can now be analyzed with AI
    return true;
  };

  const handleViewReport = (report) => {
    setViewingReport(report);
    setShowViewer(true);
  };

  const handleDownloadReport = (report) => {
    try {
      // Convert base64 to blob and download
      const byteCharacters = atob(report.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/octet-stream' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.report_name}.${getFileExtension(report.file_data)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download report');
      console.error('Download error:', error);
    }
  };

  const getFileExtension = (base64Data) => {
    // Detect file type from base64 header
    if (base64Data.startsWith('/9j/')) return 'jpg';
    if (base64Data.startsWith('iVBOR')) return 'png';
    if (base64Data.startsWith('JVBER')) return 'pdf';
    return 'file';
  };

  const getFileUrl = (report) => {
    // Create data URL from base64
    const mimeType = getMimeType(report.file_data);
    return `data:${mimeType};base64,${report.file_data}`;
  };

  const getMimeType = (base64Data) => {
    if (base64Data.startsWith('/9j/')) return 'image/jpeg';
    if (base64Data.startsWith('iVBOR')) return 'image/png';
    if (base64Data.startsWith('JVBER')) return 'application/pdf';
    return 'application/octet-stream';
  };

  // Camera functionality
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera for documents
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      setCameraStream(stream);
      setShowCamera(true);
      
      // Set video stream when component mounts
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      toast.error('Camera access denied. Please enable camera permissions.');
      console.error('Camera error:', error);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
    setCapturedImage(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      // Set canvas size to video size
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw video frame to canvas
      context.drawImage(video, 0, 0);
      
      // Convert canvas to blob
      canvas.toBlob((blob) => {
        setCapturedImage(blob);
        setFile(blob); // Set as file for upload
      }, 'image/jpeg', 0.9);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    setFile(null);
  };

  const usePhoto = () => {
    if (capturedImage) {
      stopCamera();
      toast.success('Photo captured successfully! Fill in the report details and upload.');
    }
  };

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 sm:py-8 pt-24 " data-testid="medical-reports-page">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">{t('nav.reports')}</h1>
        <p className="text-gray-600 text-lg mb-8">{t('reports.uploadAndManage')}</p>

        <Card className="p-8 bg-white shadow-xl mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('reports.uploadNewReport')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label>{t('reports.reportType')}</Label>
              <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={formData.report_type}
                onChange={(e) => setFormData({ ...formData, report_type: e.target.value })}
              >
                <option value="blood_test">{t('reports.bloodTest')}</option>
                <option value="xray">{t('reports.xRay')}</option>
                <option value="mri">{t('reports.mri')}</option>
                <option value="ct_scan">{t('reports.ctScan')}</option>
                <option value="prescription">{t('reports.prescription')}</option>
                <option value="other">{t('reports.other')}</option>
              </select>
            </div>
            <div>
              <Label>{t('reports.reportName')}</Label>
              <Input
                value={formData.report_name}
                onChange={(e) => setFormData({ ...formData, report_name: e.target.value })}
                placeholder={t('reports.reportNamePlaceholder')}
                className="mt-1"
              />
            </div>
            <div>
              <Label>{t('reports.date')}</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="md:col-span-2">
              <Label>{t('reports.uploadMethod')}</Label>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">{t('reports.selectFile')}</Label>
                  <Input
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">{t('reports.takePhoto')}</Label>
                  <Button
                    type="button"
                    onClick={startCamera}
                    variant="outline"
                    className="w-full h-10 border-2 border-dashed border-teal-300 hover:border-teal-400 hover:bg-teal-50"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {t('reports.captureReportPhoto')}
                  </Button>
                </div>
              </div>
              {file && (
                <div className="mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-teal-600" />
                    <span className="text-sm font-medium text-teal-800">
                      {file.name || 'Captured Photo'}
                    </span>
                    {file.size && (
                      <span className="text-xs text-teal-600">
                        ({Math.round(file.size / 1024)} KB)
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <Button
            onClick={handleUpload}
            disabled={uploading || !file || !formData.report_name}
            className="mt-6 w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3 disabled:opacity-50"
            data-testid="upload-report-btn"
          >
            <Upload className="w-5 h-5 mr-2" />
            {uploading ? t('reports.uploading') : t('reports.uploadReport')}
          </Button>
        </Card>

        {/* Report Statistics */}
        {reports.length > 0 && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 bg-gradient-to-br from-teal-100 to-cyan-100 border-2 border-teal-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('reports.totalReports')}</p>
                  <p className="text-2xl font-bold text-gray-800">{reports.length}</p>
                </div>
                <FileText className="w-8 h-8 text-teal-600" />
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-purple-100 to-indigo-100 border-2 border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('reports.aiAnalyzed')}</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {reports.filter(r => r.analysis).length}
                  </p>
                </div>
                <Brain className="w-8 h-8 text-purple-600" />
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-blue-100 to-cyan-100 border-2 border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('reports.bloodTests')}</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {reports.filter(r => r.report_type === 'blood_test').length}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-green-100 to-teal-100 border-2 border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">{t('reports.imaging')}</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {reports.filter(r => ['xray', 'mri', 'ct_scan'].includes(r.report_type)).length}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-green-600" />
              </div>
            </Card>
          </div>
        )}

        <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('reports.medicalReportsHistory')}</h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((report) => (
            <Card key={report.id} className="p-6 bg-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-teal-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-800 truncate" title={report.report_name}>
                    {report.report_name}
                  </h3>
                  <p className="text-xs text-gray-500 capitalize">{report.report_type.replace('_', ' ')}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <Calendar className="w-4 h-4" />
                <span>{report.date}</span>
              </div>
              
              {/* View and Download Buttons */}
              <div className="space-y-2 mb-2">
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => handleViewReport(report)}
                    variant="outline"
                    className="flex items-center gap-1 text-sm"
                    data-testid={`view-report-${report.id}`}
                  >
                    <Eye className="w-4 h-4" />
                    {t('reports.view')}
                  </Button>
                  <Button
                    onClick={() => handleDownloadReport(report)}
                    variant="outline"
                    className="flex items-center gap-1 text-sm"
                    data-testid={`download-report-${report.id}`}
                  >
                    <Download className="w-4 h-4" />
                    {t('reports.download')}
                  </Button>
                </div>
              </div>
              
              {canAnalyze(report.report_type) && (
                <div className="space-y-2">
                  <Button
                    onClick={() => handleAnalyze(report)}
                    disabled={analyzing === report.id}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:shadow-lg transition-all"
                    data-testid={`analyze-report-${report.id}`}
                  >
                    {analyzing === report.id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('reports.analyzing')}
                      </>
                    ) : (
                      <>
                        <Brain className="w-4 h-4 mr-2" />
                        {t('reports.aiAnalyze')}
                      </>
                    )}
                  </Button>
                  
                  {report.analysis && (
                    <Button
                      onClick={() => {
                        setSelectedReport(report);
                        setShowAnalysis(true);
                      }}
                      variant="outline"
                      className="w-full"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t('reports.viewAnalysis')}
                    </Button>
                  )}
                </div>
              )}
            </Card>
          ))}
          {reports.length === 0 && (
            <div className="col-span-full text-center py-16">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t('reports.noReportsYet')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Report Viewer Dialog */}
      <Dialog open={showViewer} onOpenChange={setShowViewer}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                <FileText className="w-6 h-6 text-teal-600" />
                {viewingReport?.report_name}
              </DialogTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowViewer(false)}
                className="rounded-full"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-sm text-gray-600 capitalize">
              {viewingReport?.report_type.replace('_', ' ')} • {viewingReport?.date}
            </p>
          </DialogHeader>
          
          {viewingReport && (
            <div className="mt-4 space-y-4">
              <div className="flex gap-2">
                <Button
                  onClick={() => handleDownloadReport(viewingReport)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  {t('reports.downloadReport')}
                </Button>
                {canAnalyze(viewingReport.report_type) && !viewingReport.analysis && (
                  <Button
                    onClick={() => {
                      setShowViewer(false);
                      handleAnalyze(viewingReport);
                    }}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
                  >
                    <Brain className="w-4 h-4 mr-2" />
                    {t('reports.aiAnalyze')}
                  </Button>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg border-2 border-gray-200 overflow-hidden" style={{ maxHeight: '60vh' }}>
                {getMimeType(viewingReport.file_data) === 'application/pdf' ? (
                  <iframe
                    src={getFileUrl(viewingReport)}
                    className="w-full h-full"
                    style={{ minHeight: '60vh' }}
                    title={viewingReport.report_name}
                  />
                ) : (
                  <div className="flex items-center justify-center p-4 overflow-auto" style={{ maxHeight: '60vh' }}>
                    <img
                      src={getFileUrl(viewingReport)}
                      alt={viewingReport.report_name}
                      className="max-w-full h-auto rounded-lg shadow-lg"
                      style={{ maxHeight: '55vh' }}
                    />
                  </div>
                )}
              </div>

              {viewingReport.analysis && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-2 border-purple-200">
                  <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    {t('reports.aiAnalysisAvailable')}
                  </h4>
                  <Button
                    onClick={() => {
                      setSelectedReport(viewingReport);
                      setShowAnalysis(true);
                      setShowViewer(false);
                    }}
                    variant="outline"
                    size="sm"
                  >
                    {t('reports.viewFullAnalysis')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Analysis Dialog */}
      <Dialog open={showAnalysis} onOpenChange={setShowAnalysis}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Brain className="w-6 h-6 text-purple-600" />
              {t('reports.aiMedicalImageAnalysis')}
            </DialogTitle>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-2 border-purple-200">
                <h3 className="font-bold text-gray-800 mb-1">{selectedReport.report_name}</h3>
                <p className="text-sm text-gray-600 capitalize">
                  {selectedReport.report_type.replace('_', ' ')} • {selectedReport.date}
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border-2 border-gray-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  {t('reports.aiAnalysisResults')}
                </h4>
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {selectedReport.analysis || t('reports.noAnalysis')}
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-lg">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ <strong>{t('reports.medicalDisclaimer')}</strong> {t('reports.medicalDisclaimerText')}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Camera Capture Dialog */}
      <Dialog open={showCamera} onOpenChange={() => stopCamera()}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b bg-white">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <Camera className="w-6 h-6 text-teal-600" />
                {t('reports.captureMedicalReport')}
              </DialogTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={stopCamera}
                className="rounded-full"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t('reports.positionDocument')}
            </p>
          </DialogHeader>
          
          <div className="relative bg-black flex items-center justify-center" style={{ height: '60vh' }}>
            {!capturedImage ? (
              <>
                {/* Video Feed */}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-contain"
                />
                
                {/* Camera Overlay */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Document Frame Overlay */}
                  <div className="absolute inset-8 border-2 border-white border-dashed opacity-50 rounded-lg"></div>
                  
                  {/* Corner Guidelines */}
                  <div className="absolute top-12 left-12 w-8 h-8 border-t-4 border-l-4 border-teal-400"></div>
                  <div className="absolute top-12 right-12 w-8 h-8 border-t-4 border-r-4 border-teal-400"></div>
                  <div className="absolute bottom-12 left-12 w-8 h-8 border-b-4 border-l-4 border-teal-400"></div>
                  <div className="absolute bottom-12 right-12 w-8 h-8 border-b-4 border-r-4 border-teal-400"></div>
                  
                  {/* Instructions */}
                  <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm">
                    📄 {t('reports.alignDocument')}
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Preview Captured Image */}
                <img
                  src={URL.createObjectURL(capturedImage)}
                  alt="Captured report"
                  className="w-full h-full object-contain"
                />
                
                {/* Preview Overlay */}
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  {t('reports.photoCaptured')}
                </div>
              </>
            )}
          </div>
          
          {/* Camera Controls */}
          <div className="p-6 bg-white border-t">
            {!capturedImage ? (
              <div className="flex items-center justify-center gap-4">
                <Button
                  onClick={stopCamera}
                  variant="outline"
                  className="px-6"
                >
                  {t('buttons.cancel')}
                </Button>
                <Button
                  onClick={capturePhoto}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-full text-lg font-medium"
                >
                  <Camera className="w-5 h-5 mr-2" />
                  {t('reports.capturePhoto')}
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-4">
                <Button
                  onClick={retakePhoto}
                  variant="outline"
                  className="px-6 flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  {t('reports.retake')}
                </Button>
                <Button
                  onClick={usePhoto}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-full text-lg font-medium flex items-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  {t('reports.useThisPhoto')}
                </Button>
              </div>
            )}
            
            {/* Tips */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                💡 <strong>{t('reports.tips')}</strong> {t('reports.tipsText')}
              </p>
            </div>
          </div>
          
          {/* Hidden canvas for photo capture */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicalReports;
